/**
 * ================================================================================================
 *  חזרה מעמוד התשלום — ONE SCREEN FOR BOTH JOURNEYS, BECAUSE THE QUESTION IS THE SAME QUESTION
 * ================================================================================================
 *
 * The customer has come back from Cardcom's own page. Whether they were registering or replacing a
 * card, what has to happen here is identical: ask the SERVER what the provider said.
 *
 * ── THIS SCREEN DECIDES NOTHING, AND THAT IS THE DESIGN ────────────────────────────────────────
 *
 * Cardcom's own documentation says it: do not rely on the success page, ask their servers. So
 * arriving at this address proves only that a browser arrived at this address — anybody can type a
 * URL. The screen issues one call, `public.payment.settle`, carrying the reference out of its own
 * path; the server finds the page that reference names, reads the result from Cardcom, and records
 * it. What is rendered here is the server's answer and never an inference from the URL.
 *
 * THERE IS NO `?status=success` BRANCH ANYWHERE IN THIS FILE, and that absence is the point. A
 * screen with one would be a screen that says *"אמצעי התשלום נרשם"* to whoever appends the
 * parameter.
 *
 * ── IT IS PRE-SESSION, AND IT ALSO SERVES A SIGNED-IN OFFICE ───────────────────────────────────
 *
 * A registration has no account; a card replacement is an office that does. One address serves
 * both because the settle route serves both, and the difference reaches this screen as one field:
 * `replacedPrevious`. The two "what next" links differ accordingly — back into the registration,
 * or back to the billing screen — and neither is a decision about what happened.
 *
 * ── AND IT RUNS ONCE ──────────────────────────────────────────────────────────────────────────
 *
 * `presented` is the same guard `SignupVerifyScreen` uses for the same reason: React may mount a
 * component twice in development, and a second settle would be a second provider read. The server
 * refuses it anyway — `resolvePage` is guarded on `resolved_at IS NULL` — but a screen that leans
 * on a server guard to avoid double-calling is a screen that will double-call something else.
 */

import { useEffect, useRef, useState } from 'react';

import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { StateBlock } from '../components/StateBlock';
import { callOperation } from '../api/operations';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
import { useLocale } from '../i18n/LocaleProvider';
import { routes } from '../routes';

type Stage =
  | { readonly kind: 'checking' }
  | { readonly kind: 'verified'; readonly replacedPrevious: boolean }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function PaymentReturnScreen({
  reference,
}: {
  readonly reference: string | null;
}): JSX.Element {
  const { t } = useLocale();
  const [stage, setStage] = useState<Stage>({ kind: 'checking' });
  const presented = useRef(false);

  useEffect(() => {
    if (reference === null || reference === '' || presented.current) return;
    presented.current = true;
    void (async () => {
      try {
        const settled = await callOperation('public.payment.settle', { body: { reference } });
        setStage({ kind: 'verified', replacedPrevious: settled.replacedPrevious });
      } catch (error) {
        setStage({ kind: 'failed', failure: classifyActionError(error) });
      }
    })();
  }, [reference]);

  /*
   * NO REFERENCE IS ITS OWN STATE AND NOT A FAILURE. Somebody has reached this address directly,
   * or a redirect lost the segment. Saying so, with the way back, is more useful than a refusal
   * about a request that was never made.
   */
  if (reference === null || reference === '') {
    return (
      <main className="pre-session" dir="rtl">
        <h1>{t('cardReturn.title')}</h1>
        <StateBlock
          titleHe={t('cardReturn.noReference')}
          bodyHe={t('cardReturn.noReferenceBody')}
          tone="unavailable"
          nextStep={
            <p>
              <a href={routes.tenantBilling}>{t('cardReturn.toBilling')}</a>
            </p>
          }
        />
      </main>
    );
  }

  return (
    <main className="pre-session" dir="rtl">
      <h1>{t('cardReturn.title')}</h1>

      {stage.kind === 'checking' ? (
        <p aria-live="polite">{t('cardReturn.working')}</p>
      ) : stage.kind === 'failed' ? (
        /*
         * THE SERVER'S REFUSAL, WITH ITS REASON. `card_declined` and `provider_unreadable` are
         * different refusals and the notice renders the one that happened: telling somebody their
         * card was declined when our own call to the provider timed out would be telling them
         * something false about their bank.
         */
        <>
          <ActionFailureNotice failure={stage.failure} />
          <p>
            <a href={routes.tenantBilling}>{t('cardReturn.toBilling')}</a>
          </p>
        </>
      ) : (
        <StateBlock
          titleHe={t('cardReturn.verified')}
          bodyHe={
            stage.replacedPrevious
              ? `${t('cardReturn.verifiedBody')} ${t('cardReturn.replaced')}`
              : t('cardReturn.verifiedBody')
          }
          nextStep={
            <p>
              {/*
                WHERE TO GO NEXT, DECIDED BY THE ONE FIELD THAT DISTINGUISHES THE JOURNEYS. A
                replacement came from the billing screen and goes back to it; a registration has
                no office yet, so it goes back to the public door, where the mailed link resumes
                the request. Neither link asserts anything about the outcome.
              */}
              <a href={stage.replacedPrevious ? routes.tenantBilling : routes.publicSignup}>
                {stage.replacedPrevious ? t('cardReturn.toBilling') : t('cardReturn.continue')}
              </a>
            </p>
          }
        />
      )}
    </main>
  );
}
